import { Collapse, VStack, useDisclosure } from '@chakra-ui/react';
import FeedbackDialog from '../../dialogs/FeedbackDialog';
import { OnboardingChecklistButton } from '../../onboarding/OnboardingChecklistButton';
import { OnboardingChecklistPanel } from '../../onboarding/OnboardingChecklistPanel';
import { useMobileDetection } from '../../../hooks/ui/useMobileDetection';
import { useSetChecklistVisibility } from '../../../hooks/data/useOnboardingChecklist';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { ROUTES } from '../../../constants/routes';
import { useNavigate } from 'react-router';
import { AccountMenu } from './AccountMenu';
import { HelpMenu } from './HelpMenu';
import type { useChecklistPanel } from './useChecklistPanel';

interface NavRailFooterProps {
  showChecklist: boolean;
  checklistData: ReturnType<typeof useChecklistPanel>['checklistData'];
  isChecklistPanelOpen: boolean;
  onOpenChecklistPanel: () => void;
  onCloseChecklistPanel: () => void;
}

/**
 * Bottom cluster of the nav rail: onboarding progress, help, account.
 *
 * The checklist PANEL lives here too, anchored to the rail's right edge. It has
 * to be rendered by whoever owns the button that opens it; when the old sidebar
 * (its previous home) was deleted, anything less would have left the whole
 * onboarding flow unreachable. `FeedbackDialog` stays rail-owned so the help
 * menu has one mounted feedback surface.
 */
export function NavRailFooter({
  showChecklist,
  checklistData,
  isChecklistPanelOpen,
  onOpenChecklistPanel,
  onCloseChecklistPanel,
}: NavRailFooterProps) {
  const { isMobile } = useMobileDetection();
  const feedbackDialog = useDisclosure();
  const setVisibility = useSetChecklistVisibility();
  const navigate = useNavigate();
  // A unified setup list hidden or dismissed in this workspace comes back from
  // Help. The per-user list has no way back, as before.
  // Only while showing it would show something: a completed list stays off
  // the dashboard either way. A list cached for the previous workspace (one
  // paint after a switch) is not this one's.
  const { workspace } = useWorkspace();
  const unified =
    checklistData?.forWorkspaceId === workspace?.id ? checklistData?.unified : undefined;
  const setupGuideHidden =
    !!unified &&
    !checklistData?.allCoreStepsComplete &&
    !!(unified.collapsedAt || unified.dismissedAt);
  const showSetupGuide = () => {
    // The list lives on the dashboard: take the reader there to see it.
    navigate(ROUTES.dashboard);
    setVisibility.mutate('shown');
  };

  return (
    <VStack
      spacing={1}
      align="center"
      px={2}
      pt={2}
      // Safe-area inset so the account avatar isn't hidden by device chrome.
      pb="max(0.5rem, env(safe-area-inset-bottom))"
      borderTop="1px"
      borderColor="sidebar.border"
    >
      {/* Collapse must always be mounted so the exit animation plays when the
          checklist is dismissed. unmountOnExit cleans up after. */}
      <Collapse in={showChecklist} animateOpacity unmountOnExit>
        <OnboardingChecklistButton
          completedCount={checklistData?.completedCount ?? 0}
          totalSteps={checklistData?.totalCoreSteps ?? 5}
          isComplete={checklistData?.allCoreStepsComplete ?? false}
          onOpenPanel={onOpenChecklistPanel}
        />
      </Collapse>

      <HelpMenu
        onOpenFeedback={feedbackDialog.onOpen}
        onShowSetupGuide={setupGuideHidden ? showSetupGuide : undefined}
      />
      <AccountMenu />

      <OnboardingChecklistPanel
        isOpen={isChecklistPanelOpen}
        onClose={onCloseChecklistPanel}
        checklistData={checklistData}
        isMobile={isMobile}
      />
      <FeedbackDialog isOpen={feedbackDialog.isOpen} onClose={feedbackDialog.onClose} />
    </VStack>
  );
}
